import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  ToasterProvider,
  AuthProvider,
  SessionExpiredModal,
} from '../../shared/ui';
// Global styles must load before routeTree so page CSS can override them
import './index.css';
import './app-layout.css';
import { store } from './store/store';
import { routeTree } from './routeTree.gen';
import { muiTheme } from './theme/muiTheme';
import { OfflineBanner, UpdatePrompt } from './components/pwa';
import './i18n/config'; // Initialize i18next

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Create a new router instance
const router = createRouter({
  routeTree,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ToasterProvider position="top-right">
            <AuthProvider>
              <RouterProvider router={router} />
              <SessionExpiredModal />
              <OfflineBanner />
              <UpdatePrompt />
            </AuthProvider>
          </ToasterProvider>
        </QueryClientProvider>
      </Provider>
    </ThemeProvider>
  </StrictMode>
);
